'use client';

import type { ForwardedRef, InputHTMLAttributes, ReactNode} from 'react';
import { forwardRef, useState } from 'react';
import styled from 'styled-components';

import { Close } from '@zoomit/icons';
import type { SpacingType } from '@zoomit/primitives';
import { Flex, Spacing } from '@zoomit/primitives';

import { cssVar } from '../../css/css-var';
import { theme } from '../../theme';

type InputProps = {
  variant?: 'contained' | 'rounded';
  rightNode?: ReactNode;
  leftNode?: ReactNode;
  ref?: ForwardedRef<HTMLInputElement>;
  error?: boolean;
  spacing?: SpacingType;
  wrapperCalssName?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef((props: InputProps, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    variant = 'contained',
    leftNode,
    rightNode,
    error,
    spacing = { px: 'x' },
    wrapperCalssName,
    ...rest
  } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);

  return (
    <Root
      className={wrapperCalssName}
      alignItems="center"
      flexWrap="nowrap"
      variant={variant}
      isFocused={isFocused}
      spacing={spacing}
    >
      <i className="node right-node">{rightNode}</i>
      <InputBase
        ref={ref}
        {...rest}
        onFocus={(e) => {
          setIsFocused(true);
          rest.onFocus && rest.onFocus(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          rest.onBlur && rest.onBlur(e);
        }}
      />
      <i className="node left-node">{leftNode}</i>
      {error && (
        <i className="node error">
          <Close fill={cssVar(theme.primary1)} />
        </i>
      )}
    </Root>
  );
});

type InputRootProps = {
  variant?: 'contained' | 'rounded';
  isFocused: boolean;
};

const Root = styled(Flex)<InputRootProps>`
  width: 100%;
  border-radius: ${({ variant }) => (variant === 'rounded' ? '24px' : `var(${theme.radius.sm})`)};
  border: ${({ isFocused }) => (isFocused ? `1px solid #2E2D2D` : `1px solid var(${theme.grey3})`)};

  [data-theme='light'] & {
    background-color: #ffffff;

    input {
      color: #383e43;
    }
  }

  transition: border-color 0.2s;

  [data-theme='dark'] & {
    background-color: #ffffff10;
    border: none;

    input {
      color: #ffffff;
    }
  }

  .node {
    display: flex;
    align-items: center;
  }
`;

const InputBase = styled.input`
  height: 2.8rem;
  flex-grow: 1;
  border-radius: inherit;
  font-size: 1.4rem;
  outline: none;
  border: none;
  padding: 0 ${Spacing.x2}rem;
  width: 100%;
  background-color: unset;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(${theme.grey3});
  }
`;
